/**
 * Consignment workcenter-config mass export/import modal.
 *
 * For each (customer, workcenter) pair, the Weekly Report needs a
 * `requester_id` (numeric identifier that appears on every invoice
 * line) and, optionally, a `ship_to_address` override for the
 * "Deliver to" line. This modal lets customer + platform users:
 *
 *   1. Download the CURRENT config as a CSV (Export CSV button).
 *   2. Download a blank TEMPLATE with headers + one example row.
 *   3. Fill in / edit the CSV offline (Excel / any text editor).
 *   4. Upload the edited CSV (Import CSV button) -- the modal parses
 *      client-side, previews the first 10 rows, then POSTs to the
 *      /api/reports/consignment/workcenter-config/import endpoint.
 *      Server upserts row-by-row; per-row errors surface in the
 *      result summary.
 *
 * Vendors don't see the button in the parent -- the endpoints also
 * 403 vendor callers server-side as a defense in depth.
 */
import React, { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { configService } from "../../services/configService";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Platform-only: which customer to export. Customer callers pass
   *  undefined; the server auto-scopes to their own cid. */
  customerId?: number;
}

interface ImportRow {
  customer_company_id: number | null;
  workcenter_id: number | null;
  requester_id: string;
  ship_to_address: string | null;
  note: string | null;
}

interface ImportResult {
  total: number;
  upserted: number;
  errors: number;
  results: Array<{
    row: number;
    action: "upserted" | "error";
    error?: string;
    customer_company_id?: number;
    workcenter_id?: number;
  }>;
}

const RECOGNIZED_HEADERS: Record<string, keyof ImportRow> = {
  customer_company_id: "customer_company_id",
  workcenter_id: "workcenter_id",
  requester_id: "requester_id",
  ship_to_address: "ship_to_address",
  ship_to: "ship_to_address",
  note: "note",
};

const PREVIEW_ROWS = 10;
const MAX_ROWS = 2000;

function baseUrl(): string {
  const raw = configService.getConfig().mttr.appApiBaseUrl;
  if (raw == null) throw new Error("appApiBaseUrl is unset");
  return raw.replace(/\/+$/, "");
}

function coerceCell(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  return s === "" ? null : s;
}

function parseCsvFile(text: string): ImportRow[] {
  const wb = XLSX.read(text, { type: "string" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, {
    defval: null,
    raw: false,
  });
  return rows.map((r) => {
    const bag: Record<string, string | null> = {};
    for (const [key, value] of Object.entries(r)) {
      const field = RECOGNIZED_HEADERS[key.trim().toLowerCase()];
      if (!field) continue;
      bag[field] = coerceCell(value);
    }
    const cust = bag.customer_company_id;
    const wc = bag.workcenter_id;
    return {
      customer_company_id: cust != null ? Number(cust) : null,
      workcenter_id: wc != null ? Number(wc) : null,
      requester_id: bag.requester_id ?? "",
      ship_to_address: bag.ship_to_address ?? null,
      note: bag.note ?? null,
    };
  });
}

async function downloadCsv(url: string, token: string, fallbackName: string): Promise<void> {
  const resp = await fetch(url, {
    headers: { Accept: "text/csv", "access-token": token },
    cache: "no-store",
  });
  if (!resp.ok) throw new Error(`Download failed: ${resp.status} ${resp.statusText}`);
  const blob = await resp.blob();
  // Prefer server-provided Content-Disposition filename when present.
  let name = fallbackName;
  const cd = resp.headers.get("content-disposition");
  if (cd) {
    const m = /filename="?([^";]+)"?/.exec(cd);
    if (m) name = m[1];
  }
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export const ConsignmentConfigModal: React.FC<Props> = ({
  open,
  onClose,
  customerId,
}) => {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const previewRows = useMemo(() => rows.slice(0, PREVIEW_ROWS), [rows]);

  const reset = (): void => {
    setBusy(false);
    setError(null);
    setFile(null);
    setRows([]);
    setResult(null);
    setShowAllErrors(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const csvQs =
    customerId != null ? `?customer=${encodeURIComponent(String(customerId))}` : "";

  const onExport = async (): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await downloadCsv(
        `${baseUrl()}/api/reports/consignment/workcenter-config.csv${csvQs}`,
        token,
        "consignment-workcenter-config.csv",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onTemplate = async (): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await downloadCsv(
        `${baseUrl()}/api/reports/consignment/workcenter-config/template.csv${csvQs}`,
        token,
        "consignment-workcenter-config-template.csv",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (f: File): Promise<void> => {
    setError(null);
    setResult(null);
    setFile(f);
    try {
      const text = await f.text();
      const parsed = parseCsvFile(text);
      if (parsed.length === 0) {
        setRows([]);
        setError("File has no data rows.");
        return;
      }
      if (parsed.length > MAX_ROWS) {
        setRows([]);
        setError(`Too many rows (${parsed.length}). Max ${MAX_ROWS} per import.`);
        return;
      }
      setRows(parsed);
    } catch (e) {
      setRows([]);
      setError(e instanceof Error ? e.message : "Could not parse file");
    }
  };

  const onImport = async (): Promise<void> => {
    if (!token || rows.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(
        `${baseUrl()}/api/reports/consignment/workcenter-config/import`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "access-token": token,
          },
          body: JSON.stringify({ rows }),
        },
      );
      if (!resp.ok) {
        let detail = resp.statusText;
        try {
          const j = await resp.json();
          if (j?.error) detail = j.error;
        } catch { /* fall through */ }
        throw new Error(detail);
      }
      const r = (await resp.json()) as ImportResult;
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDialogClose = (): void => {
    reset();
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onDialogClose}
    >
      <div
        className="bg-white rounded-md shadow-lg max-w-3xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b border-secondary-200 flex items-center justify-between">
          <h2 className="text-base font-semibold">
            Requesters &amp; Ship-to addresses
          </h2>
          <button
            type="button"
            onClick={onDialogClose}
            className="text-secondary-500 hover:text-secondary-800 text-lg leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="p-4 overflow-y-auto flex-1 space-y-3 text-sm">
          <p className="text-xs text-secondary-600">
            Per-workcenter <code>requester_id</code> and (optional){" "}
            <code>ship_to_address</code> for the Weekly Report. Download the
            current config, edit in Excel/any editor, then upload the same file
            to apply changes. Rows are upserted by{" "}
            <code>(customer_company_id, workcenter_id)</code>.
          </p>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => void onExport()} disabled={busy}>
              Export CSV
            </Button>
            <Button size="sm" variant="outline" onClick={() => void onTemplate()} disabled={busy}>
              Download template
            </Button>
          </div>

          <div className="border border-dashed border-secondary-300 rounded-md p-3">
            <label className="text-xs text-secondary-700">
              <div className="mb-1">Upload edited CSV</div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFile(f);
                }}
                className="text-xs"
              />
            </label>
            {file && (
              <div className="mt-2 text-[11px] text-secondary-500">
                {file.name} · {(file.size / 1024).toFixed(1)} KB · {rows.length} row
                {rows.length === 1 ? "" : "s"} detected
              </div>
            )}
          </div>

          {error && (
            <div className="text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded text-xs">
              {error}
            </div>
          )}

          {!result && rows.length > 0 && (
            <div>
              <div className="text-xs text-secondary-600 mb-1">
                Preview (first {Math.min(PREVIEW_ROWS, rows.length)} of {rows.length}):
              </div>
              <div className="border border-secondary-200 rounded overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-secondary-50 text-secondary-600 uppercase">
                    <tr>
                      <th className="px-2 py-1 text-left">Customer&nbsp;#</th>
                      <th className="px-2 py-1 text-left">Workcenter&nbsp;#</th>
                      <th className="px-2 py-1 text-left">Requester</th>
                      <th className="px-2 py-1 text-left">Ship-to address</th>
                      <th className="px-2 py-1 text-left">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {previewRows.map((r, i) => (
                      <tr key={i}>
                        <td className="px-2 py-1">{r.customer_company_id ?? ""}</td>
                        <td className="px-2 py-1">{r.workcenter_id ?? ""}</td>
                        <td className="px-2 py-1 font-mono">{r.requester_id}</td>
                        <td className="px-2 py-1">{r.ship_to_address ?? ""}</td>
                        <td className="px-2 py-1 text-secondary-500">{r.note ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-success-50 border border-success-200 rounded p-2">
                  <div className="text-secondary-600">Upserted</div>
                  <div className="text-lg font-semibold">{result.upserted}</div>
                </div>
                <div
                  className={
                    "border rounded p-2 " +
                    (result.errors > 0
                      ? "bg-error-50 border-error-200"
                      : "bg-secondary-50 border-secondary-200")
                  }
                >
                  <div className="text-secondary-600">Errors</div>
                  <div className="text-lg font-semibold">{result.errors}</div>
                </div>
              </div>
              {result.errors > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAllErrors((v) => !v)}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    {showAllErrors ? "Hide" : "Show"} errored rows
                  </button>
                  {showAllErrors && (
                    <div className="mt-1 border border-error-200 rounded p-2 max-h-52 overflow-y-auto text-[11px] space-y-1">
                      {result.results
                        .filter((r) => r.action === "error")
                        .map((r) => (
                          <div key={r.row}>
                            Row {r.row}: <span className="text-error-700">{r.error}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="px-4 py-3 border-t border-secondary-200 flex justify-end gap-2">
          {!result ? (
            <>
              <Button variant="outline" size="sm" onClick={onDialogClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={rows.length === 0 || busy}
                onClick={() => void onImport()}
              >
                {busy
                  ? "Importing…"
                  : rows.length > 0
                    ? `Import ${rows.length} row${rows.length === 1 ? "" : "s"}`
                    : "Import"}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={onDialogClose}>
              Done
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default ConsignmentConfigModal;
